import {t} from '@/i18n'
import {FormulaField, FormulaFunction, FormulaVocabulary} from "@/types/FormulaVocabulary";
import types from "@/model/types";

/**
 * O que sugerir na posição do cursor de uma fórmula — sem CodeMirror, para poder ser exercitado
 * fora da tela; o `FormulaInput` só adapta o resultado para o editor.
 *
 * **Não é um parser de CEL.** Olha para o texto antes do cursor e reconhece três situações: uma
 * seleção ou situação sendo comparada (`prioridade == `, que sugere as opções e insere o id), um
 * campo ou método sendo pedido (`cliente.ci`, `itens.`), e um nome solto. Uma fórmula que ele não entenda só fica sem sugestão — quem decide
 * o que vale é a checagem do servidor, que sublinha o erro. Errar para menos é o preço de não
 * ter uma segunda gramática no cliente.
 */

export type FormulaSuggestion = {
    label: string
    kind: 'field' | 'variable' | 'function' | 'method' | 'option' | 'keyword'
    /** O tipo, já no idioma da tela ("Número", "Item (múltiplo)"). */
    detail?: string
    /** O nome do atributo na tela ("Data do prazo"), que vai na própria linha da sugestão. */
    name?: string
    /** A explicação da função ou da variável do app, que vai num painel ao lado. */
    info?: string
    /** O texto que entra, quando não é o próprio `label` (`sum()`, o id `7` da opção "Alta"). */
    apply?: string
}

export type FormulaCompletion = { from: number, suggestions: FormulaSuggestion[] }

const TIMESTAMP = 'google.protobuf.Timestamp'
const NAME = '[A-Za-z_][A-Za-z0-9_]*'
/**
 * Um nome do caminho com os índices que o seguem (`parceria[0]`): cada índice é um elemento da lista. Sem
 * eles, `parceria[0].cancao[0].` não era caminho, e o editor não sugeria nada depois do ponto (relato do
 * usuário) — o verificador, que os entende, acusava o campo pela metade sem dizer qual seria.
 */
const SEGMENT = `${NAME}(?:\\[[^\\[\\]]*\\])*`
const PATH = `${SEGMENT}(?:\\.${SEGMENT})*`
const KEYWORDS = ['true', 'false', 'null', 'in']

/** As macros que dão nome ao elemento: `itens.map(i, ...)`. */
const COMPREHENSION = new RegExp(`(${PATH})\\.(?:map|filter|all|exists|exists_one)\\(\\s*(${NAME})\\s*,`, 'g')

/** Os nomes de um caminho, cada um com os índices dele: `a[0].b` → `a[0]`, `b` — o ponto dentro do índice fica. */
function segmentsOf(path: string): string[] {
    return [...path.matchAll(new RegExp(SEGMENT, 'g'))].map(match => match[0])
}

/**
 * A explicação de cada função do app, na sugestão. A chave inteira, e não montada: o `t` confere
 * a chave pelo tipo. As que faltam aqui aparecem só com o tipo.
 */
const DOCS = {
    sum: 'formula-doc.sum',
    avg: 'formula-doc.avg',
    median: 'formula-doc.median',
    min: 'formula-doc.min',
    max: 'formula-doc.max',
    distinct: 'formula-doc.distinct',
    size: 'formula-doc.size',
    map: 'formula-doc.map',
    filter: 'formula-doc.filter',
    all: 'formula-doc.all',
    exists: 'formula-doc.exists',
    daysBetween: 'formula-doc.daysBetween',
    filled: 'formula-doc.filled',
    empty: 'formula-doc.empty',
    asList: 'formula-doc.asList',
    getFullYear: 'formula-doc.getFullYear',
    getMonth: 'formula-doc.getMonth',
    getDate: 'formula-doc.getDate',
    today: 'formula-doc.today',
    now: 'formula-doc.now',
    //Só nas regras: o editor o acrescenta ao vocabulário no modo regra
    self: 'formula-doc.self',
    _id: 'formula-doc.id',
    _state: 'formula-doc.state',
    _createdAt: 'formula-doc.created-at',
    _updatedAt: 'formula-doc.updated-at',
    _stateChangedAt: 'formula-doc.state-changed-at',
    _createdBy: 'formula-doc.created-by',
    _updatedBy: 'formula-doc.updated-by',
    currentUser: 'formula-doc.current-user',
    //O campo de toda pessoa: os aliases dos papéis dela no ambiente
    roles: 'formula-doc.roles',
    //O acesso do item para quem pede — ver o design da coleção
    _visible: 'formula-doc.visible',
    _editable: 'formula-doc.editable',
    _deletable: 'formula-doc.deletable'
} as const

function doc(name: string): string | undefined {
    const key = DOCS[name as keyof typeof DOCS]
    return key ? t(key) as string : undefined
}

/** O elemento de uma lista: `list<Item_1>` → `Item_1`. */
export function elementOf(type: string): string | undefined {
    const match = /^list<(.+)>$/.exec(type)
    return match ? match[1] : undefined
}

/**
 * O tipo de uma coleção num nível (`Cliente_1`) — o servidor desenrola um tipo por nível, e é o
 * sufixo que os distingue dos tipos do CEL.
 */
const isCollection = (type: string) => /_\d+$/.test(type)

/** De tipo do CEL para tipo da aplicação — o `property.type` que o seletor "Tipo" oferece. */
const APP_TYPES: Record<string, string> = {
    double: 'number', int: 'number', uint: 'number', string: 'text', bool: 'boolean', [TIMESTAMP]: 'date'
}

/**
 * Um tipo com o múltiplo, como o app o escreve: "Texto (múltiplo)" — ou só o tipo, quando não é
 * múltiplo. Era a mesma frase montada em quatro lugares — aqui, na mensagem de tipo errado e no
 * resultado dos dois editores de propriedade.
 */
export function multipleLabel(label: string, multiple = true): string {
    return multiple ? `${label} (${(t('multiple') as string).toLowerCase()})` : label
}

/**
 * O tipo no idioma da tela, **pelos nomes que o app já usa**: o do seletor "Tipo" do atributo
 * (`types`), "Situação" e "Múltiplo". Um vocabulário à parte de tipos ("número", "lista de…") era
 * uma segunda forma de chamar a mesma coisa. `collection` é o nome da coleção, quando o tipo é dela.
 *
 * Vazio quando não se sabe (`dyn`): é melhor não dizer nada do que dizer "valor".
 */
export function formulaTypeLabel(type: string | undefined, collection?: string): string {
    if (!type || type === 'dyn') return ''
    const element = elementOf(type)
    if (element) {
        const inner = formulaTypeLabel(element, collection)
        return inner ? multipleLabel(inner) : t('multiple') as string
    }
    if (APP_TYPES[type]) return types[APP_TYPES[type]].name
    if (type === 'State') return t('state') as string
    if (type === 'Option') return types.choice.name
    if (type === 'User') return types.user.name
    if (isCollection(type)) return collection || type.replace(/_\d+$/, '')
    return type
}

/**
 * Esta função pode ser chamada sobre este tipo? `list<dyn>` (o `map`, o `size`) serve para
 * qualquer lista; e uma lista de tipo desconhecido — o resultado de um `map` — aceita as funções
 * de qualquer lista, porque o editor não sabe o que ela contém.
 */
function receives(receiver: string, type: string): boolean {
    if (receiver === type || receiver === 'dyn') return true
    const wanted = elementOf(receiver)
    const element = elementOf(type)
    if (wanted === undefined || element === undefined) return false
    return wanted === 'dyn' || element === 'dyn'
}

type Resolved = { type?: string, field?: FormulaField }

/**
 * O tipo — e o campo, quando é um — de um caminho como `cliente.cidade` ou `itens[0].produto`, a partir da
 * raiz (`segmentsOf`). Cada índice troca a lista pelo elemento dela.
 */
function resolve(vocabulary: FormulaVocabulary, segments: string[], scope: Record<string, string>): Resolved {
    let field: FormulaField | undefined
    segments.forEach((segment, position) => {
        const [name, ...indexes] = segment.split('[')
        if (position === 0) field = scope[name] ? {type: scope[name]} : vocabulary.variables[name]
        else {
            const fields: Record<string, FormulaField> | undefined = field && vocabulary.types[field.type]
            field = fields ? fields[name] : undefined
        }
        for (let index = 0; index < indexes.length && field; index++) {
            const element = elementOf(field.type)
            field = element ? {type: element} : undefined
        }
    })
    return {type: field && field.type, field}
}

/**
 * As variáveis das macros abertas antes do cursor: em `itens.map(i, i.`, `i` é um Item.
 *
 * Todas as macros escritas antes contam, mesmo as já fechadas — sugerir `i` depois do fim do
 * `map` é sobrar uma sugestão; não sugeri-lo dentro dele seria faltar a mais útil.
 */
function scopeOf(vocabulary: FormulaVocabulary, before: string): Record<string, string> {
    const scope: Record<string, string> = {}
    for (const match of before.matchAll(COMPREHENSION)) {
        const {type} = resolve(vocabulary, segmentsOf(match[1]), scope)
        const element = type && elementOf(type)
        if (element) scope[match[2]] = element
    }
    return scope
}

/** O tipo que sai de uma chamada terminada no fim de `before` (`itens.map(i, i.preco)`). */
function callResult(vocabulary: FormulaVocabulary, before: string): string | undefined {
    let depth = 0
    for (let index = before.length - 1; index >= 0; index--) {
        if (before[index] === ')') depth++
        else if (before[index] === '(' && --depth === 0) {
            const name = new RegExp(`(${NAME})$`).exec(before.slice(0, index))
            const called = name && vocabulary.functions.find(candidate => candidate.name === name[1])
            return called ? called.returns : undefined
        }
    }
    return undefined
}

/** Funções agrupadas pelo nome: as sobrecargas são uma sugestão só. */
function functionSuggestions(functions: FormulaFunction[], kind: 'function' | 'method'): FormulaSuggestion[] {
    const byName = new Map<string, FormulaFunction[]>()
    for (const candidate of functions) byName.set(candidate.name, [...(byName.get(candidate.name) || []), candidate])
    return [...byName.entries()].map(([name, overloads]) => ({
        label: name,
        kind,
        //O tipo que sai: a explicação diz o resto, e listar as sobrecargas seria ruído
        detail: formulaTypeLabel(overloads[0].returns),
        info: doc(name),
        //Sem argumento nenhum em todas as formas, a chamada entra fechada (`sum()`); com algum,
        //entra aberta, para o cursor ficar onde se escreve o argumento
        apply: overloads.every(overload => !overload.params.length) ? `${name}()` : `${name}(`
    }))
}

function fieldSuggestion(name: string, field: FormulaField, kind: 'field' | 'variable'): FormulaSuggestion {
    return {
        label: name,
        kind,
        detail: formulaTypeLabel(field.type, field.collection),
        name: field.label,
        info: doc(name)
    }
}

function membersOf(vocabulary: FormulaVocabulary, type: string): FormulaSuggestion[] {
    const fields = vocabulary.types[type] || {}
    return [
        ...Object.entries(fields).map(([name, field]) => fieldSuggestion(name, field, 'field')),
        ...functionSuggestions(vocabulary.functions.filter(candidate =>
            candidate.receiver && receives(candidate.receiver, type)), 'method')
    ]
}

/** O cursor está dentro de um texto? Conta as aspas abertas antes dele. */
function insideText(before: string): boolean {
    const quotes = (before.replace(/\\./g, '').match(/"/g) || []).length
    const apostrophes = (before.replace(/\\./g, '').match(/'/g) || []).length
    return quotes % 2 === 1 || apostrophes % 2 === 1
}

/**
 * As sugestões na posição `cursor` de `text`, a partir de `from` — ou `null` quando não há o que
 * sugerir ali. Não filtra pelo que já foi digitado: isso é do editor, que casa por aproximação.
 */
export function completionsAt(vocabulary: FormulaVocabulary, text: string, cursor: number): FormulaCompletion | null {
    const before = text.slice(0, cursor)
    const scope = scopeOf(vocabulary, before)

    //Uma seleção ou situação comparada: as opções, que a fórmula cita pelo ALIAS, entre aspas
    //(`prioridade == "alta"`). A sugestão se mostra e se filtra pelo NOME — é por ele que se procura —
    //e o que entra é o alias. Vem antes do "dentro de texto": depois da aspa aberta
    //(`prioridade == "al`) é justamente onde se quer a lista, e aí entra só o resto, com a aspa que
    //fecha. Sem a aspa, o outro lado pode ser também um nome (`_state == _state`, `prioridade ==
    //cliente.prioridade`), e os nomes soltos vêm junto: só as opções, `_s` não achava nada (relato do
    //usuário). O campo `alias` compara como a própria situação ou opção (`_state.alias == "`), como no
    //servidor. Comparação com o que não tem opções segue adiante
    const compared = new RegExp(`(${NAME}(?:\\.${NAME})*)\\s*[!=]=\\s*(?:"([^"]*)|([A-Za-z0-9_]*))$`).exec(before)
    const path = compared ? compared[1].split('.') : []
    const cited = path.length > 1 && path[path.length - 1] === 'alias' ? path.slice(0, -1) : path
    const comparedField = compared && resolve(vocabulary, cited, scope).field
    if (compared && comparedField && comparedField.options) {
        const quoted = compared[2] !== undefined
        const options: FormulaSuggestion[] = comparedField.options.map(option => ({
            label: option.name, kind: 'option', detail: option.alias,
            apply: quoted ? `${option.alias}"` : JSON.stringify(option.alias)
        }))
        return {
            from: cursor - (quoted ? compared[2] : compared[3]).length,
            suggestions: quoted ? options : [...options, ...bareSuggestions(vocabulary, scope)]
        }
    }
    if (insideText(before)) return null

    //Um campo ou método: depois de um caminho (`cliente.ci`) ou de uma chamada (`map(...).`)
    const member = new RegExp(`(${PATH}|\\))\\.(${NAME})?$`).exec(before)
    if (member) {
        const partial = member[2] || ''
        const type = member[1] === ')'
            ? callResult(vocabulary, before.slice(0, member.index + 1))
            : resolve(vocabulary, segmentsOf(member[1]), scope).type
        if (!type) return null
        return {from: cursor - partial.length, suggestions: membersOf(vocabulary, type)}
    }

    //Um nome solto: os campos da coleção, as variáveis das macros, as funções e as palavras
    const bare = new RegExp(`(?:^|[^A-Za-z0-9_.])(${NAME})?$`).exec(before)
    if (!bare) return null
    const partial = bare[1] || ''
    return {from: cursor - partial.length, suggestions: bareSuggestions(vocabulary, scope)}
}

/** Os nomes soltos: os campos da coleção, as variáveis das macros, as funções e as palavras. */
function bareSuggestions(vocabulary: FormulaVocabulary, scope: Record<string, string>): FormulaSuggestion[] {
    return [
        ...Object.entries(vocabulary.variables).map(([name, field]) => fieldSuggestion(name, field, 'variable')),
        ...Object.entries(scope).map(([name, type]) => fieldSuggestion(name, {type}, 'variable')),
        ...functionSuggestions(vocabulary.functions.filter(candidate => !candidate.receiver), 'function'),
        ...KEYWORDS.map(keyword => ({label: keyword, kind: 'keyword' as const}))
    ]
}
